import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Loader2,
  Mail,
  MailWarning,
  Phone,
  Star,
  X,
} from 'lucide-react';
import { api } from '@/lib/api';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Modal from '@/components/ui/Modal';
import BloqueTareas from '@/components/tareas/BloqueTareas';
import {
  CANAL_LABELS,
  ESTADO_BADGES,
  ESTADO_LABELS,
  EXPECTATIVAS_LABELS,
  LICENCIA_LABELS,
  MOTIVO_DESCARTE_LABELS,
  NUM_PROPIEDADES_LABELS,
  TIPO_UNIDAD_LABELS,
  ZONA_LABELS,
  esDecidible,
  etiqueta,
  type Peticion,
} from '@/lib/peticiones';

/**
 * Ficha de una petición: todo lo que contó el propietario, sus fotos y la
 * decisión.
 *
 * Pensada para revisarse desde el móvil, que es como llega la mayoría —por el
 * enlace del aviso de Telegram—: una columna, fotos en rejilla de dos, y los
 * botones de decidir fijos abajo para no tener que volver arriba después de
 * mirar las fotos.
 */

function classNames(...c: (string | false | undefined)[]): string {
  return c.filter(Boolean).join(' ');
}

function fechaLarga(iso?: string): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('es-ES', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function Dato({ etiqueta: label, children }: { etiqueta: string; children?: React.ReactNode }) {
  if (!children) return null;
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-sereno-dark/40">{label}</dt>
      <dd className="text-sm mt-0.5">{children}</dd>
    </div>
  );
}

export default function PeticionDetallePage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();

  const [peticion, setPeticion] = useState<Peticion | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [accion, setAccion] = useState<'aceptar' | 'rechazar' | null>(null);
  const [nota, setNota] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [errorAccion, setErrorAccion] = useState<string | null>(null);
  const [reenviando, setReenviando] = useState(false);
  const [ampliada, setAmpliada] = useState<number | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      setPeticion(await api.getPeticion(id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error de conexión');
    } finally {
      setCargando(false);
    }
  }, [id]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const decidir = async () => {
    if (!accion) return;
    setGuardando(true);
    setErrorAccion(null);
    try {
      const actualizada =
        accion === 'aceptar'
          ? await api.aceptarPeticion(id, nota || undefined)
          : await api.rechazarPeticion(id, nota || undefined);
      setPeticion(actualizada);
      setAccion(null);
      setNota('');
    } catch (err) {
      setErrorAccion(err instanceof Error ? err.message : 'No se ha podido guardar');
    } finally {
      setGuardando(false);
    }
  };

  const reenviarCorreo = async () => {
    setReenviando(true);
    try {
      setPeticion(await api.reenviarCorreoPeticion(id));
    } catch (err) {
      setErrorAccion(err instanceof Error ? err.message : 'No se ha podido reenviar');
    } finally {
      setReenviando(false);
    }
  };

  if (cargando) return <LoadingSpinner />;

  if (error || !peticion) {
    return (
      <div>
        <button onClick={() => navigate('/leads/peticiones')} className="btn-ghost flex items-center gap-2 text-sm mb-4">
          <ArrowLeft size={16} /> Volver
        </button>
        <div className="card bg-red-50 border-red-200 text-red-700 text-sm">
          No se ha podido cargar la petición: {error}
        </div>
      </div>
    );
  }

  const p = peticion;
  const fotos = p.fotos ?? [];
  const decidible = esDecidible(p.estado);

  return (
    <div className="pb-28 md:pb-0">
      <Link to="/leads/peticiones" className="btn-ghost inline-flex items-center gap-2 text-sm mb-4">
        <ArrowLeft size={16} /> Peticiones
      </Link>

      {/* Cabecera */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-6">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {p.esProyectoEspecial && <Star size={18} className="text-amber-500" aria-label="Proyecto especial" />}
            <h1 className="text-xl sm:text-2xl font-bold">{p.nombre || 'Sin nombre'}</h1>
            <span className={classNames('badge', ESTADO_BADGES[p.estado])}>{ESTADO_LABELS[p.estado]}</span>
          </div>
          <p className="text-sm text-sereno-dark/40 mt-1">Recibida el {fechaLarga(p.createdAt)}</p>
        </div>
        {p.leadId && (
          <Link to={`/leads/propietarios?lead=${p.leadId}`} className="btn-ghost text-sm shrink-0 self-start">
            Ver en el pipeline
          </Link>
        )}
      </div>

      {p.esProyectoEspecial && (
        <div className="card bg-amber-50 border-amber-200 mb-4">
          <p className="text-sm text-amber-900 flex items-start gap-2">
            <Star size={16} className="mt-0.5 shrink-0" />
            <span>
              Proyecto especial. No pasó por el filtro de licencia ni de fotos: la cualificación se hace
              hablando con el propietario.
            </span>
          </p>
        </div>
      )}

      {p.duplicadoDe && (
        <div className="card bg-blue-50 border-blue-200 mb-4">
          <p className="text-sm text-blue-900 flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span>
              {p.duplicadoDe.tipo === 'owner'
                ? 'Este contacto ya es propietario de Sereno. No se creó lead nuevo.'
                : 'Ya existía un lead con este email o teléfono; la petición se enlazó a él en vez de duplicarlo.'}
            </span>
          </p>
        </div>
      )}

      {/* Contacto: acciones directas, que en el móvil es desde donde se llama */}
      <div className="card mb-4">
        <h2 className="font-medium mb-3">Contacto</h2>
        <div className="flex flex-col sm:flex-row gap-2">
          {p.telefono && (
            <a href={`tel:${p.telefono}`} className="btn-primary flex items-center justify-center gap-2 h-11 text-sm">
              <Phone size={16} /> {p.telefono}
            </a>
          )}
          {p.email && (
            <a href={`mailto:${p.email}`} className="btn-ghost flex items-center justify-center gap-2 h-11 text-sm">
              <Mail size={16} /> {p.email}
            </a>
          )}
        </div>
      </div>

      {/* Respuestas */}
      <div className="card mb-4">
        <h2 className="font-medium mb-3">Lo que nos ha contado</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Dato etiqueta="Propiedades">{etiqueta(NUM_PROPIEDADES_LABELS, p.numPropiedades)}</Dato>
          <Dato etiqueta="Zona">{etiqueta(ZONA_LABELS, p.zona)}</Dato>
          <Dato etiqueta="Licencia">{etiqueta(LICENCIA_LABELS, p.licencia)}</Dato>
          <Dato etiqueta="Tipo">{etiqueta(TIPO_UNIDAD_LABELS, p.tipoUnidad)}</Dato>
          <Dato etiqueta="Dirección">{p.direccion}</Dato>
          <Dato etiqueta="Expectativas">{etiqueta(EXPECTATIVAS_LABELS, p.expectativas)}</Dato>
          <Dato etiqueta="Nos conoció por">
            {p.canal === 'otro' && p.canalOtroDetalle
              ? `${etiqueta(CANAL_LABELS, p.canal)} — ${p.canalOtroDetalle}`
              : etiqueta(CANAL_LABELS, p.canal)}
          </Dato>
          <Dato etiqueta="Motivo del descarte">{etiqueta(MOTIVO_DESCARTE_LABELS, p.motivoDescarte)}</Dato>
        </dl>

        {(p.descripcion || p.proyectoDetalle || p.zonaOtroDetalle) && (
          <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
            {p.descripcion && (
              <div>
                <dt className="text-xs uppercase tracking-wide text-sereno-dark/40 mb-1">Descripción</dt>
                <p className="text-sm whitespace-pre-wrap">{p.descripcion}</p>
              </div>
            )}
            {p.proyectoDetalle && (
              <div>
                <dt className="text-xs uppercase tracking-wide text-sereno-dark/40 mb-1">El proyecto</dt>
                <p className="text-sm whitespace-pre-wrap">{p.proyectoDetalle}</p>
              </div>
            )}
            {p.zonaOtroDetalle && (
              <div>
                <dt className="text-xs uppercase tracking-wide text-sereno-dark/40 mb-1">Su propuesta</dt>
                <p className="text-sm whitespace-pre-wrap">{p.zonaOtroDetalle}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Fotos */}
      {fotos.length > 0 && (
        <div className="card mb-4">
          <h2 className="font-medium mb-3">Fotos ({fotos.length})</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {fotos.map((_, i) => (
              <button
                key={i}
                onClick={() => setAmpliada(i)}
                className="aspect-square rounded-lg overflow-hidden bg-slate-100 hover:opacity-90 transition-opacity"
                aria-label={`Ampliar foto ${i + 1}`}
              >
                <img
                  src={api.urlFotoPeticion(p.id, i)}
                  alt={`Foto ${i + 1} de la propiedad`}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Decisión ya tomada */}
      {p.decision && (
        <div className="card mb-4">
          <h2 className="font-medium mb-2">Decisión</h2>
          <p className="text-sm">
            {p.estado === 'aceptada' ? 'Aceptada' : 'Rechazada'} por {p.decision.por} el{' '}
            {fechaLarga(p.decision.at)}.
          </p>
          {p.decision.nota && (
            <p className="text-sm text-sereno-dark/60 mt-2 whitespace-pre-wrap">
              Nota interna: {p.decision.nota}
            </p>
          )}

          {p.correo && (
            <div className="mt-3 pt-3 border-t border-gray-100 text-sm flex items-start gap-2">
              {p.correo.estado === 'enviado' ? (
                <>
                  <Mail size={16} className="mt-0.5 text-green-600 shrink-0" />
                  <span className="text-sereno-dark/60">Correo enviado al propietario.</span>
                </>
              ) : (
                <>
                  <MailWarning size={16} className="mt-0.5 text-amber-600 shrink-0" />
                  <div>
                    <span className="text-sereno-dark/60">
                      {p.correo.estado === 'no_configurado'
                        ? 'El correo no se envió: el envío todavía no está configurado.'
                        : `El correo no se pudo enviar: ${p.correo.detalle || 'error desconocido'}`}
                    </span>
                    <button
                      onClick={reenviarCorreo}
                      disabled={reenviando}
                      className="btn-ghost text-sm mt-2 flex items-center gap-2 h-11"
                    >
                      {reenviando && <Loader2 size={14} className="animate-spin" />}
                      Reintentar el envío
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {errorAccion && (
        <div className="card bg-red-50 border-red-200 text-red-700 text-sm mb-4">{errorAccion}</div>
      )}

      {/* Tareas del lead de esta petición. Al aceptarla se crea la de llamar en
          24-48 h, que es la promesa del correo: aquí se ve si sigue pendiente
          sin salir de la petición. */}
      <div className="card mb-4">
        <BloqueTareas
          relatedType="lead"
          relatedId={p.leadId || p.id}
          relatedName={p.nombre || null}
          titulo="Tareas"
        />
      </div>

      {/* Acciones. Fijas abajo en móvil: tras mirar las fotos, decidir queda a mano. */}
      {decidible && (
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200 p-3 flex gap-2 lg:ml-56 md:static md:border-0 md:bg-transparent md:p-0 md:z-auto">
          <button
            onClick={() => { setAccion('aceptar'); setErrorAccion(null); }}
            className="btn-primary flex-1 md:flex-none flex items-center justify-center gap-2 h-12 md:h-11 text-sm"
          >
            <Check size={18} /> Aceptar
          </button>
          <button
            onClick={() => { setAccion('rechazar'); setErrorAccion(null); }}
            className="flex-1 md:flex-none flex items-center justify-center gap-2 h-12 md:h-11 px-5 rounded-xl border border-red-200 text-red-600 bg-white hover:bg-red-50 text-sm font-medium transition-colors"
          >
            <X size={18} /> Rechazar
          </button>
        </div>
      )}

      {/* Confirmación */}
      <Modal
        isOpen={accion !== null}
        onClose={() => !guardando && setAccion(null)}
        title={accion === 'aceptar' ? 'Aceptar la petición' : 'Rechazar la petición'}
      >
        <div className="space-y-4">
          <p className="text-sm text-sereno-dark/70">
            {accion === 'aceptar'
              ? 'Se enviará al propietario un correo diciéndole que le llamamos en 24–48 h, se creará la tarea de llamada y su lead pasará a "Primera llamada".'
              : 'Se enviará al propietario un correo comunicándole que ahora no podemos incorporar su propiedad, y su lead pasará a "Descartado".'}
          </p>
          <div>
            <label htmlFor="nota-decision" className="block text-sm font-medium mb-1">
              Nota interna <span className="text-sereno-dark/40 font-normal">(opcional)</span>
            </label>
            <textarea
              id="nota-decision"
              rows={3}
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              placeholder="Por qué se toma esta decisión. No se incluye en el correo."
              className="input w-full resize-none"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setAccion(null)} disabled={guardando} className="btn-ghost h-11 text-sm">
              Cancelar
            </button>
            <button
              onClick={decidir}
              disabled={guardando}
              className={classNames(
                'h-11 px-5 rounded-xl text-sm font-medium text-white flex items-center gap-2',
                accion === 'aceptar' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700',
              )}
            >
              {guardando && <Loader2 size={16} className="animate-spin" />}
              {accion === 'aceptar' ? 'Aceptar' : 'Rechazar'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Foto ampliada */}
      {ampliada !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setAmpliada(null)}
          role="dialog"
          aria-label="Foto ampliada"
        >
          <button
            onClick={() => setAmpliada(null)}
            className="absolute top-4 right-4 h-11 w-11 rounded-full bg-white/10 text-white flex items-center justify-center"
            aria-label="Cerrar"
          >
            <X size={20} />
          </button>
          <img
            src={api.urlFotoPeticion(p.id, ampliada)}
            alt={`Foto ${ampliada + 1} de la propiedad`}
            className="max-h-full max-w-full object-contain"
          />
        </div>
      )}
    </div>
  );
}
